import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { productsAPI } from '../services/api';
import ProductCard from './ProductCard';
import { Button } from './ui/button';

const PreferredProducts = () => {
  const { t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPreferredProducts = async () => {
      try {
        setLoading(true);
        const preferredProducts = await productsAPI.getPreferred(8);
        setProducts(preferredProducts);
      } catch (err) {
        console.error('Error fetching preferred products:', err);
        setError('Failed to load preferred products');
      } finally {
        setLoading(false);
      }
    };

    fetchPreferredProducts();
  }, []);

  if (loading) {
    return (
      <section className="py-16 bg-gradient-to-br from-capsule-bg via-white to-capsule-bg-dark/20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('preferred_products')}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, index) => (
              <div key={index} className="animate-pulse">
                <div className="bg-gray-200 rounded-lg h-64 mb-4"></div>
                <div className="h-4 bg-gray-200 rounded mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-16 bg-gradient-to-br from-capsule-bg via-white to-capsule-bg-dark/20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center">
            <p className="text-red-600">{error}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-gradient-to-br from-capsule-bg via-white to-capsule-bg-dark/20">
      <div className="max-w-7xl mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t('preferred_products')}
          </h2>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
          {products.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>

        {/* View All Button */}
        <div className="text-center">
          <Link to="/products">
            <Button 
              className="bg-gradient-to-r from-capsule-teal to-capsule-aqua hover:from-capsule-teal/90 hover:to-capsule-aqua/90 text-white px-8 py-3 rounded-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl"
              data-testid="view-all-preferred-btn"
            >
              {t('view_all')}
            </Button>
          </Link>
        </div>
        
        {/* Quality Text Section */}
        <div className="mt-20 text-center">
          <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
            {t('highest_quality_title')}
          </h3>
          <p className="text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed">
            {t('highest_quality_text')}
          </p>
        </div>
      </div>
    </section>
  );
};

export default PreferredProducts;